import clsx from "clsx";
import { FC, useCallback, useRef, useState } from "react";
import { noop } from "lodash";

import { ApprovalsTab } from "@/constants";
import {
  EmptyDataScreen,
  ErrorScreen,
  IconButton,
  NavigationTabs,
} from "@/components";
import { ResultListItemSkeleton } from "@/components/skeletons/results-list-skeleton";
import { Search } from "@/assets/icons";
import { InboxItem } from "@/services/api/inbox/contracts";
import { useInfiniteScroll } from "@/hooks";
import { useSearchParams } from "react-router-dom";

import { FiltersChip } from "../filters-chip";
import { SidebarItem } from "./sidebar-item";
import { SidebarProps } from "./types";
import { ApprovalsFilter } from "@/pages/inbox/components/filters";
import { SearchForm } from "@/pages/inbox/components/search-form";
import { SearchFieldType } from "@/pages/inbox/components/search-field";
import { SearchSuggestions } from "@/pages/inbox/components/search-suggestions";
import { useSuggestionsList } from "@/pages/inbox/hooks/use-suggestions-list";
import { InboxKeyParam } from "@/pages/inbox/types";
import { useInboxTabs } from "@/pages/inbox/components/sidebar/hooks/use-inbox-tabs";

import styles from "./styles.scss";

type InboxArrayWithTotal = InboxItem[] & { __totalCount?: number };

const hasInboxTotal = (value: unknown): value is InboxArrayWithTotal =>
  Array.isArray(value) &&
  Object.prototype.hasOwnProperty.call(value, "__totalCount");

export const SideBar: FC<SidebarProps & { totalCount?: number | null }> = ({
  data,
  isLoading,
  isError,
  isFilterMode,
  approvalSuggests,
  onSetApprovalSuggests,
  searchContainerRef,
  isTracking,
  isSearchFocus,
  searchQuery,
  filters,
  appliedFilters,
  onSetSearchQuery,
  onToggleSearchFieldFocus,
  onFocusSearchSuggests,
  onSelectRequest,
  onQueryChange,
  onQueryClear,
  onSearchSubmit,
  onSetFilters,
  onResetFilters,
  onApplyFilters,
  onUnApplyFilters,
  onLoadMoreData,
  hasMoreData,
  isShowFullSearchList,
  totalCount,
}): JSX.Element => {
  const [searchParams] = useSearchParams();

  const effectiveTotalCount =
    totalCount ??
    (hasInboxTotal(data) ? data.__totalCount : undefined) ??
    (Array.isArray(data) ? data.length : 0);

  const approvalTabs = useInboxTabs(effectiveTotalCount);
  const [isSearchMode, setIsSearchMode] = useState<boolean>(false);

  const listRef = useRef<HTMLUListElement | null>(null);

  const { loadTriggerRef: scrollControlElementRef } =
    useInfiniteScroll<HTMLLIElement>({
      loadData: onLoadMoreData ?? noop,
      isLoading: Boolean(isLoading),
      shouldLoad: Boolean(hasMoreData),
    });

  const ReferenceNumber = searchParams.get(InboxKeyParam.ReferenceNumber);

  const {
    isLoading: isLoadingSuggests,
    data: suggests,
    error,
  } = useSuggestionsList({
    tab: ApprovalsTab.Tracking,
    searchQuery,
    isTracking,
    appliedFilters: appliedFilters.join(","),
    isShowFullSearchList,
  });

  const renderSideBarContent = useCallback((): JSX.Element => {
    if (isError) {
      return (
        <div className={styles.noResultsContainer}>
          <ErrorScreen />
        </div>
      );
    }

    if (
      !isLoading &&
      !(data && data.length > 0) &&
      !(approvalSuggests && approvalSuggests.length > 0)
    ) {
      return (
        <div className={styles.noResultsContainer}>
          <EmptyDataScreen />
        </div>
      );
    }

    return (
      <ul className={styles.list} ref={listRef}>
        {approvalSuggests && approvalSuggests.length > 0 ? (
          <>
            {approvalSuggests.map((item, index) => (
              <SidebarItem
                key={
                  item.ReferenceNumber ??
                  item.RequestId ??
                  `approval-suggest-${index}`
                }
                ReferenceNumber={ReferenceNumber}
                item={item}
                onSelectRequest={() => onSelectRequest(item)}
              />
            ))}
          </>
        ) : (
          <>
            {(data ?? []).map((item, index) => (
              <SidebarItem
                key={item.ReferenceNumber ?? item.RequestId ?? index}
                ReferenceNumber={ReferenceNumber}
                item={item}
                onSelectRequest={onSelectRequest}
              />
            ))}
          </>
        )}

        {isLoading && (
          <div>
            <ResultListItemSkeleton />
            <ResultListItemSkeleton />
            <ResultListItemSkeleton />
          </div>
        )}

        {hasMoreData && !isLoading && (
          <li style={{ height: "1px" }} ref={scrollControlElementRef} />
        )}
      </ul>
    );
  }, [
    isError,
    isLoading,
    data,
    approvalSuggests,
    hasMoreData,
    scrollControlElementRef,
    ReferenceNumber,
    onSelectRequest,
  ]);

  const handleSelectTab = useCallback((): void => {
    onSetApprovalSuggests?.(null);
    setIsSearchMode(false);
    onSetSearchQuery?.("");
  }, [onSetApprovalSuggests, onSetSearchQuery]);

  const handleSelectSuggestion = useCallback(
    (item: InboxItem): void => {
      onSelectRequest(item);
      onSetApprovalSuggests?.([item]);
      onToggleSearchFieldFocus?.(false);
    },
    [onSelectRequest, onSetApprovalSuggests, onToggleSearchFieldFocus],
  );

  return (
    <div
      className={clsx(
        styles.container,
        !!searchParams.get(InboxKeyParam.ReferenceNumber) && styles.selected,
      )}
    >
      <div className={styles.panel}>
        <NavigationTabs<ApprovalsTab>
          items={approvalTabs}
          onClick={handleSelectTab}
        />
        <div className={styles.panelButtonsWrapper}>
          <IconButton
            icon={Search}
            className={clsx(
              isSearchMode && styles.panelButtonSelected,
              styles.iconWrapper,
            )}
            onClick={() => setIsSearchMode(prev => !prev)}
            size="small"
          />

          <ApprovalsFilter
            filters={filters}
            onSetFilters={onSetFilters}
            onResetFilters={onResetFilters}
            isFilterMode={isFilterMode}
            onApplyFilters={onApplyFilters}
          />
        </div>
      </div>

      <FiltersChip
        filtersCount={appliedFilters?.length || 0}
        isMobile={false}
        onFiltersClear={onUnApplyFilters}
      />

      {isSearchMode && (
        <div className={styles.searchFormWrapper} ref={searchContainerRef}>
          <SearchForm
            onSubmit={onSearchSubmit}
            className={styles.searchFieldContainer}
            onQueryClear={onQueryClear}
            onChange={onQueryChange}
            onFocus={onFocusSearchSuggests}
            searchText={searchQuery}
            searchFieldType={SearchFieldType.Gray}
            clearButtonClassName={styles.clearButton}
          />

          {!!searchQuery.length && isSearchFocus && (
            <SearchSuggestions
              isLoading={isLoadingSuggests}
              error={!!error}
              data={suggests}
              isEmptySearchForm={searchQuery.length === 0}
              onSelect={item => handleSelectSuggestion(item)}
            />
          )}
        </div>
      )}

      {renderSideBarContent()}
    </div>
  );
};
